import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { MultipleImageUpload } from '../../components/ui/image-upload';
import { productsAPI, categoriesAPI, settingsAPI } from '../../lib/api';
import { getImageUrl } from '../../lib/utils';
import { fetchWithCache, clearAllCaches } from '../../lib/cache';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Package, Upload, Download, FileSpreadsheet, Barcode, RefreshCcw, ChevronLeft, ChevronRight, Check, ChevronsUpDown } from 'lucide-react';
import { usePopup } from '../../contexts/PopupContext';
import JsBarcode from 'jsbarcode';
import JSZip from 'jszip';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { cn } from '../../lib/utils';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [limit, setLimit] = useState(20);
  const [inputPage, setInputPage] = useState('');

  const [, setSettings] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const { showPopup } = usePopup();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    sku: '',
    upc_code: '',
    mrp: '',
    selling_price: '',
    pos_price: '',
    wholesale_price: '',
    wholesale_min_qty: '10',
    max_discount_amount: '',
    cost_price: '',
    stock_qty: '0',
    low_stock_threshold: '10',
    gst_rate: '18',
    hsn_code: '',
    images: [],
    is_active: true,
    is_active_home: true,
    is_active_pos: true,
    uom: 'Unit',
    minimum_purchase_qty: '1',
    packing_charges: '',
  });

  const fetchData = useCallback(async (page = 1, searchQuery = search, catId = categoryId) => {
    // Start auxiliary fetches concurrently
    const categoriesPromise = categoriesAPI.getFlat()
      .then(res => setCategories(res.data || []))
      .catch(err => {
        toast.error('Failed to load categories');
        return [];
      });

    const settingsPromise = settingsAPI.get()
      .then(res => setSettings(res.data))
      .catch(err => {
        console.error('Failed to fetch settings:', err);
        return null; // Return null so Promise.all doesn't reject
      });

    try {
      // Create a unique cache key that includes the search query and category
      const cacheKey = searchQuery || catId !== 'all'
        ? `admin_products_search_${searchQuery}_cat_${catId}_page_${page}_limit_${limit}`
        : `admin_products_list_page_${page}_limit_${limit}`;

      // Start product fetch immediately with cache
      await fetchWithCache(
        cacheKey,
        async () => {
          const productsRes = await productsAPI.getAll({
            limit: limit,
            page: page,
            include_inactive: true,
            search: searchQuery,
            category_id: catId !== 'all' ? catId : undefined,
            light_mode: false, // Fetch all columns for admin
            t: Date.now() // BUST BROWSER HTTP CACHE
          });
          return productsRes.data;
        },
        (data, isFromCache) => {
          setProducts(data.products || []);
          setTotalPages(data.pages || 1);
          setTotalProducts(data.total || 0);
          setCurrentPage(page);
          if (isFromCache) setLoading(false);
        }
      );
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }

    // Optional: wait for others to complete if we want fetchData promise to effectively mean "all data loaded"
    await Promise.all([categoriesPromise, settingsPromise]);
  }, [search, categoryId, limit]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(1, search, categoryId);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, categoryId, fetchData]);

  // Fetch products when limit changes
  useEffect(() => {
    fetchData(1);
  }, [limit, fetchData]);

  // Initial data fetch
  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  // Keyboard shortcuts for pagination
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if not typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            if (currentPage > 1) {
              fetchData(currentPage - 1);
            }
            break;
          case 'ArrowRight':
            e.preventDefault();
            if (currentPage < totalPages) {
              fetchData(currentPage + 1);
            }
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages, fetchData]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category_id: '',
      sku: '',
      upc_code: '',
      mrp: '',
      selling_price: '',
      pos_price: '',
      wholesale_price: '',
      wholesale_min_qty: '10',
      max_discount_amount: '',
      cost_price: '',
      stock_qty: '0',
      low_stock_threshold: '10',
      gst_rate: '18',
      hsn_code: '',
      color: '',
      material: '',
      origin: '',
      images: [],
      is_active: true,
      is_active_home: true,
      is_active_pos: true,
      uom: 'Unit',
      minimum_purchase_qty: '1',
      packing_charges: '',
    });
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    console.log('🔍 DEBUG: Product data for editing:', product);
    console.log('🔍 DEBUG: Product images:', product.images);

    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      category_id: product.category_id,
      sku: product.sku,
      upc_code: product.upc_code || '',
      mrp: String(product.mrp),
      selling_price: String(product.selling_price),
      pos_price: String(product.pos_price || ''),
      wholesale_price: String(product.wholesale_price || ''),
      wholesale_min_qty: String(product.wholesale_min_qty || 10),
      max_discount_amount: product.max_discount_amount !== null && product.max_discount_amount !== undefined ? String(product.max_discount_amount) : '',
      cost_price: String(product.cost_price),
      stock_qty: String(product.stock_qty),
      low_stock_threshold: String(product.low_stock_threshold || 10),
      gst_rate: String(product.gst_rate || 18),
      hsn_code: product.hsn_code || '',
      color: product.color || '',
      material: product.material || '',
      origin: product.origin || '',
      images: Array.isArray(product.images) ? product.images : [],
      is_active: product.is_active,
      is_active_home: product.is_active_home !== undefined ? product.is_active_home : true,
      is_active_pos: product.is_active_pos !== undefined ? product.is_active_pos : true,
      uom: product.uom || 'Unit',
      minimum_purchase_qty: String(product.minimum_purchase_qty || 1),
      packing_charges: String(product.packing_charges || ''),
    });

    console.log('🔍 DEBUG: Form data after setting:', {
      images: Array.isArray(product.images) ? product.images : []
    });

    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('🔍 DEBUG: Form data before submit:', formData);
    console.log('🔍 DEBUG: Images in form data:', formData.images);

    const payload = {
      ...formData,
      mrp: parseFloat(formData.mrp),
      selling_price: parseFloat(formData.selling_price),
      pos_price: formData.pos_price ? parseFloat(formData.pos_price) : null,
      wholesale_price: formData.wholesale_price ? parseFloat(formData.wholesale_price) : null,
      wholesale_min_qty: parseInt(formData.wholesale_min_qty),
      max_discount_amount: formData.max_discount_amount !== '' && formData.max_discount_amount !== null ? parseFloat(formData.max_discount_amount) : null,
      cost_price: parseFloat(formData.cost_price),
      stock_qty: parseInt(formData.stock_qty),
      low_stock_threshold: parseInt(formData.low_stock_threshold),
      gst_rate: parseFloat(formData.gst_rate),
      uom: formData.uom,
      minimum_purchase_qty: parseInt(formData.minimum_purchase_qty) || 1,
      packing_charges: formData.packing_charges ? parseFloat(formData.packing_charges) : 0,
    };

    console.log('🔍 DEBUG: Payload before API call:', payload);
    console.log('🔍 DEBUG: Images in payload:', payload.images);

    try {
      if (editingProduct) {
        const updatePayload = {
          ...payload,
          images: Array.isArray(payload.images) ? payload.images : []
        };
        console.log('🔍 DEBUG: Update payload:', updatePayload);
        console.log('🔍 DEBUG: Update images:', updatePayload.images);

        await productsAPI.update(editingProduct.id, updatePayload);
        toast.success('Product updated successfully!');
      } else {
        const createPayload = {
          ...payload,
          images: Array.isArray(payload.images) ? payload.images : []
        };
        console.log('🔍 DEBUG: Create payload:', createPayload);
        console.log('🔍 DEBUG: Create images:', createPayload.images);

        await productsAPI.create(createPayload);
        toast.success('Product created successfully!');
      }
      setShowDialog(false);
      resetForm();
      clearAllCaches(); // Invalidate cache to show updated data
      fetchData(currentPage);
    } catch (error) {
      console.error('🔍 DEBUG: API Error:', error);
      console.error('🔍 DEBUG: Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (id) => {
    showPopup({
      title: "Delete Product",
      message: "Are you sure you want to delete this product?",
      type: "error",
      onConfirm: async () => {
        try {
          await productsAPI.delete(id);
          toast.success('Product deleted');
          clearAllCaches(); // Invalidate cache
          fetchData(currentPage);
        } catch (error) {
          toast.error('Failed to delete product');
        }
      }
    });
  };

  const generateBarcode = (product) => {
    try {
      // Create a canvas element
      const canvas = document.createElement('canvas');

      // Generate barcode using SKU or UPC Code
      const code = product.upc_code || product.sku;
      JsBarcode(canvas, code, {
        format: 'CODE128',
        width: 2,
        height: 100,
        displayValue: true,
        fontSize: 14,
        margin: 10
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `barcode-${product.sku}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Barcode generated for ${product.name}`);
      });
    } catch (error) {
      console.error('Barcode generation error:', error);
      toast.error('Failed to generate barcode');
    }
  };

  const downloadTemplate = () => {
    // Create a more comprehensive template with examples - matching export fields
    const csvHeaders = [
      'name', 'sku', 'upc_code', 'description', 'category', 'mrp', 'selling_price', 'pos_price', 'cost_price',
      'wholesale_price', 'wholesale_min_qty', 'max_discount_amount', 'stock_qty', 'low_stock_threshold', 'gst_rate',
      'hsn_code', 'color', 'material', 'origin', 'uom', 'minimum_purchase_qty', 'packing_charges',
      'is_active', 'is_active_home', 'is_active_pos'
    ];

    const sampleData = [
      [
        'Sample T-Shirt',
        'TSH001',
        '123456789012',
        'Comfortable cotton t-shirt for daily wear',
        categories.length > 0 ? categories[0].name : 'Electronics', // Use name instead of ID
        '800',
        '650',
        '600',
        '400',
        '500',
        '10',
        '75',
        '100',
        '10',
        '12',
        'HSN6109',
        'Blue',
        'Cotton',
        'India',
        'true'
      ],
      [
        'Premium Jeans',
        'JNS001',
        '',
        'High-quality denim jeans with perfect fit',
        categories.length > 1 ? categories[1].name : 'Clothing', // Use name instead of ID
        '2000',
        '1600',
        '1550',
        '1200',
        '1400',
        '5',
        '200',
        '50',
        '5',
        '12',
        'HSN6203',
        'Black',
        'Denim',
        'USA',
        'true'
      ]
    ];

    const csvContent = [
      csvHeaders.join(','),
      ...sampleData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products_bulk_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded with sample data');
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const isCSV = lowerName.endsWith('.csv');
    const isXLSX = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xlsm');
    const isXLS = lowerName.endsWith('.xls');

    if (!isCSV && !isXLSX && !isXLS) {
      toast.error('Please upload .csv, .xlsx, or .xls file');
      return;
    }

    if (isXLS) {
      toast.error('Legacy .xls is not supported directly. Save as .xlsx or .csv and upload.');
      return;
    }

    setBulkUploading(true);
    setUploadProgress({ current: 0, total: 0 });

    try {
      setUploadProgress({ current: 1, total: 4 }); // Step 1: File read

      const normalizeCSVLine = (line) => line.map((v) => String(v ?? '').replace(/"/g, '').trim());

      // Simple CSV parser that handles quoted fields
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];

          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };

      const parseXlsxRows = async (xlsxFile) => {
        const zipBuffer = await xlsxFile.arrayBuffer();
        const zip = await JSZip.loadAsync(zipBuffer);

        const workbookXml = await zip.file('xl/workbook.xml')?.async('string');
        if (!workbookXml) {
          throw new Error('Invalid Excel file: workbook.xml not found');
        }

        const workbookDoc = new DOMParser().parseFromString(workbookXml, 'application/xml');
        const firstSheet = workbookDoc.querySelector('sheet');
        const sheetRid = firstSheet?.getAttribute('r:id');

        const relsXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
        const relsDoc = relsXml ? new DOMParser().parseFromString(relsXml, 'application/xml') : null;
        const relTarget = sheetRid && relsDoc
          ? Array.from(relsDoc.querySelectorAll('Relationship')).find((r) => r.getAttribute('Id') === sheetRid)?.getAttribute('Target')
          : null;

        let worksheetPath = relTarget ? `xl/${relTarget.replace(/^\/?xl\//, '')}` : null;
        if (!worksheetPath || !zip.file(worksheetPath)) {
          const firstWorksheet = Object.keys(zip.files).find((name) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(name));
          worksheetPath = firstWorksheet || null;
        }

        if (!worksheetPath) {
          throw new Error('Invalid Excel file: worksheet not found');
        }

        const sharedStringsXml = await zip.file('xl/sharedStrings.xml')?.async('string');
        const sharedStrings = [];
        if (sharedStringsXml) {
          const sharedDoc = new DOMParser().parseFromString(sharedStringsXml, 'application/xml');
          sharedDoc.querySelectorAll('si').forEach((si) => {
            const text = Array.from(si.querySelectorAll('t')).map((t) => t.textContent || '').join('');
            sharedStrings.push(text);
          });
        }

        const sheetXml = await zip.file(worksheetPath)?.async('string');
        if (!sheetXml) {
          throw new Error('Invalid Excel file: failed to read worksheet');
        }
        const sheetDoc = new DOMParser().parseFromString(sheetXml, 'application/xml');
        const rows = [];

        const colRefToIndex = (ref) => {
          const letters = (ref || '').replace(/[0-9]/g, '').toUpperCase();
          let idx = 0;
          for (let i = 0; i < letters.length; i++) {
            idx = (idx * 26) + (letters.charCodeAt(i) - 64);
          }
          return Math.max(0, idx - 1);
        };

        sheetDoc.querySelectorAll('sheetData row').forEach((rowNode) => {
          const rowValues = [];
          rowNode.querySelectorAll('c').forEach((cell) => {
            const ref = cell.getAttribute('r') || '';
            const colIndex = colRefToIndex(ref);
            const type = cell.getAttribute('t');
            const valueNode = cell.querySelector('v');
            let value = '';

            if (type === 's') {
              const sharedIdx = parseInt(valueNode?.textContent || '', 10);
              value = Number.isNaN(sharedIdx) ? '' : (sharedStrings[sharedIdx] || '');
            } else if (type === 'inlineStr') {
              value = cell.querySelector('is t')?.textContent || '';
            } else {
              value = valueNode?.textContent || '';
            }
            rowValues[colIndex] = String(value).trim();
          });
          rows.push(rowValues.map((v) => String(v ?? '').trim()));
        });

        return rows.filter((row) => row.some((v) => v !== ''));
      };

      let rows = [];
      if (isCSV) {
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        rows = lines.map((line) => normalizeCSVLine(parseCSVLine(line)));
      } else if (isXLSX) {
        rows = await parseXlsxRows(file);
      }

      if (rows.length < 2) {
        toast.error('File must contain a header row and at least one data row');
        return;
      }

      const headerAliases = {
        'name': 'name',
        'sku': 'sku',
        'upc code': 'upc_code',
        'upc_code': 'upc_code',
        'description': 'description',
        'category': 'category',
        'mrp': 'mrp',
        'selling price': 'selling_price',
        'selling_price': 'selling_price',
        'pos price': 'pos_price',
        'pos_price': 'pos_price',
        'cost price': 'cost_price',
        'cost_price': 'cost_price',
        'wholesale price': 'wholesale_price',
        'wholesale_price': 'wholesale_price',
        'min wholesale qty': 'wholesale_min_qty',
        'wholesale_min_qty': 'wholesale_min_qty',
        'max discount %': 'max_discount_amount',
        'max_discount_amount': 'max_discount_amount',
        'stock': 'stock_qty',
        'stock_qty': 'stock_qty',
        'low stock threshold': 'low_stock_threshold',
        'low_stock_threshold': 'low_stock_threshold',
        'gst rate': 'gst_rate',
        'gst_rate': 'gst_rate',
        'hsn code': 'hsn_code',
        'hsn_code': 'hsn_code',
        'color': 'color',
        'material': 'material',
        'origin': 'origin',
        'uom': 'uom',
        'min purchase qty': 'minimum_purchase_qty',
        'minimum_purchase_qty': 'minimum_purchase_qty',
        'packing charges': 'packing_charges',
        'packing_charges': 'packing_charges',
        'image links': 'image_links',
        'image_links': 'image_links',
        'images': 'image_links',
        'image_url': 'image_links',
        'active': 'is_active',
        'is_active': 'is_active',
        'home active': 'is_active_home',
        'is_active_home': 'is_active_home',
        'pos active': 'is_active_pos',
        'is_active_pos': 'is_active_pos',
      };
      const headers = rows[0].map(h => {
        const cleaned = String(h || '').trim();
        const normalized = cleaned.toLowerCase();
        return headerAliases[normalized] || normalized;
      });
      const products = [];
      let skippedRows = 0;

      setUploadProgress({ current: 2, total: 4 }); // Step 2: Parsing

      for (let i = 1; i < rows.length; i++) {
        const values = rows[i].map((v) => String(v ?? '').trim());
        if (values.length !== headers.length) {
          skippedRows++;
          continue;
        }

        const product = {};

        // Find category ID from name
        const categoryName = values[headers.indexOf('category')];
        let categoryId = null;
        if (categoryName) {
          const cat = categories.find(c => c.name.toLowerCase() === categoryName.toLowerCase())
            || categories.find(c => c.id === categoryName); // Fallback to ID match
          if (cat) categoryId = cat.id;
        }

        headers.forEach((header, index) => {
          const value = values[index];
          if (header === 'category') {
            product['category_id'] = categoryId;
            return;
          }
          if (header === 'image_links') {
            const raw = String(value || '').trim();
            if (!raw) return;
            try {
              if (raw.startsWith('[')) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                  product.images = parsed.map((u) => String(u || '').trim()).filter(Boolean);
                  return;
                }
              }
            } catch (_) {
              // fall through to delimiter parsing
            }
            product.images = raw
              .split('|')
              .map((u) => u.trim())
              .filter(Boolean);
            return;
          }

          // Convert data types
          if (['mrp', 'selling_price', 'pos_price', 'cost_price', 'wholesale_price', 'gst_rate', 'max_discount_amount'].includes(header)) {
            product[header] = value && value !== '' ? parseFloat(value) : null;
          } else if (['wholesale_min_qty', 'stock_qty', 'low_stock_threshold'].includes(header)) {
            product[header] = value && value !== '' ? parseInt(value) : (header === 'wholesale_min_qty' ? 10 : header === 'low_stock_threshold' ? 10 : 0);
          } else if (['color', 'material', 'origin', 'upc_code'].includes(header)) {
            product[header] = value || null;
          } else if (['is_active', 'is_active_home', 'is_active_pos'].includes(header)) {
            const normalizedBool = String(value || '').toLowerCase();
            product[header] = ['true', '1', 'yes', 'y'].includes(normalizedBool);
          } else {
            product[header] = value || (header === 'gst_rate' ? 18 : null);
          }
        });

        // Validate required fields
        if (product.name && product.sku && product.mrp && product.selling_price && product.cost_price) {
          // Set default values for optional fields
          if (!product.gst_rate) product.gst_rate = 18;
          if (!product.wholesale_min_qty) product.wholesale_min_qty = 10;
          if (!product.low_stock_threshold) product.low_stock_threshold = 10;
          if (product.stock_qty === null) product.stock_qty = 0;
          if (product.is_active === undefined) product.is_active = true;
          if (product.is_active_home === undefined) product.is_active_home = true;
          if (product.is_active_pos === undefined) product.is_active_pos = true;
          if (!Array.isArray(product.images)) product.images = [];

          products.push(product);
        } else {
          skippedRows++;
        }
      }

      if (products.length === 0) {
        toast.error('No valid products found in CSV file. Please check required fields: name, sku, mrp, selling_price, cost_price');
        return;
      }

      setUploadProgress({ current: 3, total: 4 }); // Step 3: Validation complete

      const response = await productsAPI.bulkUpload(products);

      setUploadProgress({ current: 4, total: 4 }); // Step 4: Upload complete

      let message = `Processed ${products.length} products`;
      if (response.data?.created) message += ` (${response.data.created} added`;
      else message += ' (0 added';

      if (response.data?.skipped) message += `, ${response.data.skipped} skipped)`;
      else if (response.data?.updated) message += `, ${response.data.updated} updated)`;
      else message += ')';

      if (skippedRows > 0) {
        message += ` • ${skippedRows} rows invalid`;
      }

      if (response.data?.errors && response.data.errors.length > 0) {
        message += ` • ${response.data.errors.length} errors`;
        console.warn('Bulk upload errors:', response.data.errors);
      }

      toast.success(message);
      setShowBulkDialog(false);
      clearAllCaches(); // Invalidate cache
      fetchData(currentPage);

      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload products. Please check your CSV/Excel format.');
    } finally {
      setBulkUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const [selectedProducts, setSelectedProducts] = useState(new Set());

  const toggleProductSelection = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const toggleAllSelection = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  // Auto-generate SKU when opening dialog for new product
  useEffect(() => {
    const generateSku = async () => {
      if (showDialog && !editingProduct) {
        try {
          // Find latest number in SKU-AM series across all pages.
          let maxNum = 2999;
          const regex = /^SKU-(?:AM|AS)(\d+)$/i;
          let page = 1;
          let pages = 1;

          while (page <= pages) {
            const res = await productsAPI.getAll({ limit: 100, page, include_inactive: true });
            const currentProducts = res.data?.products || [];
            pages = res.data?.pages || 1;

            for (const p of currentProducts) {
              const sku = (p?.sku || '').trim();
              const match = sku.match(regex);
              if (match?.[1]) {
                const n = parseInt(match[1], 10);
                if (!Number.isNaN(n) && n > maxNum) maxNum = n;
              }
            }

            page += 1;
          }

          setFormData(prev => ({ ...prev, sku: `SKU-AM${maxNum + 1}` }));
        } catch (error) {
          console.error("Error generating SKU:", error);
          // Fallback
          setFormData(prev => ({ ...prev, sku: 'SKU-AM3000' }));
        }
      }
    };

    generateSku();
  }, [showDialog, editingProduct]);

  const handleExportAll = async () => {
    const toastId = toast.loading('Exporting all products...');
    try {
      let allProducts = [];
      let page = 1;
      let hasMore = true;

      // Fetch all pages
      while (hasMore) {
        const res = await productsAPI.getAll({ limit: 100, page: page, include_inactive: true, light_mode: false, t: Date.now() });
        if (!res.data.products || res.data.products.length === 0) break;

        allProducts = [...allProducts, ...res.data.products];
        if (page >= res.data.pages) hasMore = false;
        page++;
      }

      // Convert to CSV
      // Convert to CSV
      // Export in the same schema accepted by bulk upload so this file can be
      // edited and re-uploaded for mass updates.
      const headers = [
        'name', 'sku', 'upc_code', 'description', 'category', 'mrp', 'selling_price', 'pos_price', 'cost_price',
        'wholesale_price', 'wholesale_min_qty', 'max_discount_amount', 'stock_qty', 'low_stock_threshold', 'gst_rate',
        'hsn_code', 'color', 'material', 'origin', 'uom', 'minimum_purchase_qty', 'packing_charges', 'image_links',
        'is_active', 'is_active_home', 'is_active_pos'
      ];

      const csvContent = [
        headers.join(','),
        ...allProducts.map(p => [
          `"${(p.name || '').replace(/"/g, '""')}"`,
          p.sku,
          `"${(p.upc_code || '').replace(/"/g, '""')}"`,
          `"${(p.description || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
          `"${(categories.find(c => c.id === p.category_id)?.name || '').replace(/"/g, '""')}"`,
          p.mrp,
          p.selling_price,
          p.pos_price || '',
          p.cost_price,
          p.wholesale_price || '',
          p.wholesale_min_qty || '',
          p.max_discount_amount !== null && p.max_discount_amount !== undefined ? p.max_discount_amount : '',
          p.stock_qty,
          p.low_stock_threshold || '',
          p.gst_rate || 18,
          `"${(p.hsn_code || '').replace(/"/g, '""')}"`,
          `"${(p.color || '').replace(/"/g, '""')}"`,
          `"${(p.material || '').replace(/"/g, '""')}"`,
          `"${(p.origin || '').replace(/"/g, '""')}"`,
          `"${(p.uom || 'Unit').replace(/"/g, '""')}"`,
          p.minimum_purchase_qty || 1,
          p.packing_charges || 0,
          `"${(Array.isArray(p.images) ? p.images : []).join('|').replace(/"/g, '""')}"`,
          p.is_active !== false ? 'true' : 'false',
          p.is_active_home !== false ? 'true' : 'false',
          p.is_active_pos !== false ? 'true' : 'false'
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `products_update_sheet_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${allProducts.length} products`, { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error('Failed to export products', { id: toastId });
    }
  };

  const handleToggleStatus = async (productId, currentStatus) => {
    try {
      // Optimistically update UI
      setProducts(products.map(p =>
        p.id === productId ? { ...p, is_active: currentStatus } : p
      ));

      await productsAPI.update(productId, { is_active: currentStatus });
      toast.success(`Product ${currentStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      // Revert on failure
      setProducts(products.map(p =>
        p.id === productId ? { ...p, is_active: !currentStatus } : p
      ));
      toast.error('Failed to update status');
    }
  };

  const handleRecoverMissingImages = async () => {
    const toastId = toast.loading('Recovering missing images...');
    try {
      const response = await productsAPI.recoverMissingImages();
      const data = response.data || {};
      toast.success(
        `Recovered ${data.recovered || 0} images. Still missing: ${data.still_missing || 0}`,
        { id: toastId }
      );
      clearAllCaches();
      fetchData(currentPage, search, categoryId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recover missing images', { id: toastId });
    }
  };

  const downloadCatalog = async () => {
    const toastId = toast.loading("Generating catalog... This may take a while.");

    try {
      // Use backend API to generate PDF
      const response = await productsAPI.downloadCatalog();

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `catalog_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url); // Clean up

      toast.success("Catalog downloaded successfully!", { id: toastId });

    } catch (error) {
      console.error("Catalog generation failed:", error);
      toast.error("Failed to generate catalog", { id: toastId });
    }
  };

  // Client-side filtering removed in favor of Server-side search
  const filteredProducts = products;

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Products</h1>
          <p className="text-slate-400">
            {products.length} products
            {selectedProducts.size > 0 && ` • ${selectedProducts.size} selected`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              setRefreshing(true);
              await fetchData(currentPage, search, categoryId);
              setRefreshing(false);
            }}
            className="btn-admin"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleRecoverMissingImages}
            className="btn-admin border-amber-600 text-amber-500 hover:bg-amber-600/10"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Recover Images
          </Button>
          {selectedProducts.size > 0 ? (
            <Button variant="secondary" onClick={downloadCatalog} className="bg-primary hover:bg-primary/90 text-white border-none">
              <Download className="w-4 h-4 mr-2" />
              Download Selected ({selectedProducts.size})
            </Button>
          ) : (
            <Button variant="secondary" onClick={downloadCatalog} className="bg-blue-600 hover:bg-blue-700 text-white border-none">
              <Download className="w-4 h-4 mr-2" />
              Download Catalog
            </Button>
          )}
          <Button variant="outline" onClick={handleExportAll} className="btn-admin border-green-600 text-green-500 hover:bg-green-600/10">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Download Update Sheet
          </Button>
          <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-admin">
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-slate-800 border-slate-700" aria-describedby="bulk-upload-desc">
              <div id="bulk-upload-desc" className="sr-only">
                Dialog for bulk uploading products using CSV or Excel
              </div>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Bulk Upload Products
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div className="text-sm text-slate-400">
                  Upload multiple products at once using CSV or Excel file. Existing SKUs will be updated, new SKUs will be created.
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">1</div>
                    <div>
                      <p className="font-medium text-sm">Download Sheet</p>
                      <p className="text-xs text-slate-400">Download current products with all values, edit in Excel, then upload back</p>
                      <div className="mt-2 flex gap-2">
                        <Button
                          onClick={handleExportAll}
                          variant="outline"
                          size="sm"
                          className="border-green-600 text-green-500 hover:bg-green-600/10"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Current Products Sheet
                        </Button>
                        <Button
                          onClick={downloadTemplate}
                          variant="outline"
                          size="sm"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Blank Template
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">2</div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Fill Your Data</p>
                      <p className="text-xs text-slate-400">Edit the template with your product information</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">3</div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Upload File</p>
                      <p className="text-xs text-slate-400">Select your completed CSV or Excel file to upload</p>
                      <div className="mt-2">
                        <Input
                          type="file"
                          accept=".csv,.xlsx,.xls,.xlsm"
                          onChange={handleBulkUpload}
                          disabled={bulkUploading}
                          className="input-admin"
                        />
                      </div>
                    </div>
                  </div>

                  {bulkUploading && (
                    <div className="space-y-3 p-4 bg-blue-900/20 border border-blue-800 rounded-lg">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm text-blue-300">
                          {uploadProgress.current === 1 && "Reading file..."}
                          {uploadProgress.current === 2 && "Parsing product data..."}
                          {uploadProgress.current === 3 && "Validating products..."}
                          {uploadProgress.current === 4 && "Uploading to server..."}
                        </span>
                      </div>
                      {uploadProgress.total > 0 && (
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-3 text-xs text-slate-500 bg-slate-700/20 p-3 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-400 mb-1">Required Fields:</p>
                    <p>name, sku, mrp, selling_price, cost_price</p>
                  </div>
                  <div>
                    <p className="font-medium text-slate-400 mb-1">Optional Fields:</p>
                    <p>description, category_id, wholesale_price, wholesale_min_qty, max_discount_amount, stock_qty, low_stock_threshold, gst_rate, hsn_code, color, material, origin, image_links, is_active</p>
                  </div>
                  <div>
                    <p className="font-medium text-slate-400 mb-1">Tips:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Use the "View Category IDs" button to get valid category IDs</li>
                      <li>Set is_active to 'true' or 'false'</li>
                      <li>GST rate defaults to 18% if not specified</li>
                      <li>Stock quantity defaults to 0 if not specified</li>
                    </ul>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-admin bg-primary hover:bg-primary/90" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-invisible bg-slate-800 border-slate-700" aria-describedby="product-form-desc">
              <div id="product-form-desc" className="sr-only">
                Form to add or edit product details
              </div>
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Product Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter product name"
                      className="input-admin"
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SKU *</Label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                      placeholder="e.g., PRD001"
                      className="input-admin"
                      required
                      disabled={!!editingProduct}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Product description"
                    className="input-admin min-h-[80px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    {/* modal: popover is portaled outside the Dialog, whose scroll lock would otherwise block wheel scrolling */}
                    <Popover modal open={isCategoryOpen} onOpenChange={(open) => {
                      setIsCategoryOpen(open);
                      if (!open) setCategorySearch('');
                    }}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={isCategoryOpen}
                          className={cn(
                            "w-full justify-between input-admin font-normal",
                            !formData.category_id && "text-muted-foreground"
                          )}
                        >
                          {formData.category_id
                            ? categories.find(c => c.id === formData.category_id)?.full_path || "Select category"
                            : "Select category"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0 bg-slate-800 border-slate-700" align="start">
                        <div className="flex flex-col h-[320px]">
                          {/* Fixed Search Header */}
                          <div className="flex-shrink-0 p-3 border-b border-slate-700 bg-slate-800">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input
                                placeholder="Search category..."
                                className="pl-10 h-9 bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                value={categorySearch}
                                onChange={(e) => setCategorySearch(e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Scrollable Category List */}
                          <div className="flex-1 overflow-y-auto p-2">
                            {categories
                              .filter(cat =>
                                !categorySearch || cat.full_path.toLowerCase().includes(categorySearch.toLowerCase())
                              )
                              .length === 0 ? (
                              <div className="py-8 text-center text-sm text-slate-400">
                                {categorySearch ? 'No categories found' : 'No categories available'}
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {categories
                                  .filter(cat =>
                                    !categorySearch || cat.full_path.toLowerCase().includes(categorySearch.toLowerCase())
                                  )
                                  .map((cat) => (
                                    <div
                                      key={cat.id}
                                      onClick={() => {
                                        setFormData({ ...formData, category_id: cat.id });
                                        setIsCategoryOpen(false);
                                        setCategorySearch('');
                                      }}
                                      className="flex items-center p-2 rounded-md hover:bg-slate-700 cursor-pointer transition-colors"
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4 text-primary",
                                          formData.category_id === cat.id ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <span
                                        style={{ paddingLeft: `${cat.level * 12}px` }}
                                        className="flex-1 text-sm text-slate-100"
                                      >
                                        {cat.name}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>GST Rate (%)</Label>
                    <Select
                      value={formData.gst_rate}
                      onValueChange={(value) => setFormData({ ...formData, gst_rate: value })}
                    >
                      <SelectTrigger className="input-admin">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0%</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="18">18%</SelectItem>
                        <SelectItem value="28">28%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <Input
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      placeholder="e.g. Red"
                      className="input-admin"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Material</Label>
                    <Input
                      value={formData.material}
                      onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                      placeholder="e.g. Cotton"
                      className="input-admin"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Origin</Label>
                    <Input
                      value={formData.origin}
                      onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                      placeholder="e.g. India"
                      className="input-admin"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>UOM (Unit of Measure)</Label>
                    <Input
                      value={formData.uom}
                      onChange={(e) => setFormData({ ...formData, uom: e.target.value })}
                      placeholder="e.g. Unit, Kg, Box"
                      className="input-admin"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Purchase Qty</Label>
                    <Input
                      type="number"
                      value={formData.minimum_purchase_qty}
                      onChange={(e) => setFormData({ ...formData, minimum_purchase_qty: e.target.value })}
                      placeholder="1"
                      className="input-admin"
                    />
                    <p className="text-xs text-slate-400">Default quantity when added to cart</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>HSN Code</Label>
                    <Input
                      value={formData.hsn_code}
                      onChange={(e) => setFormData({ ...formData, hsn_code: e.target.value })}
                      className="input-admin"
                      placeholder="HSN Code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>UPC Code</Label>
                    <Input
                      value={formData.upc_code}
                      onChange={(e) => setFormData({ ...formData, upc_code: e.target.value })}
                      className="input-admin"
                      placeholder="Barcode / UPC"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>MRP (₹) *</Label>
                    <Input
                      type="number"
                      value={formData.mrp}
                      onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                      placeholder="0"
                      className="input-admin"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Selling Price (₹) *</Label>
                    <Input
                      type="number"
                      value={formData.selling_price}
                      onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
                      placeholder="0"
                      className="input-admin"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>POS Price (₹)</Label>
                    <Input
                      type="number"
                      value={formData.pos_price}
                      onChange={(e) => setFormData({ ...formData, pos_price: e.target.value })}
                      placeholder="Optional"
                      className="input-admin"
                    />
                    <p className="text-xs text-slate-400">Overrides selling price in POS only</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Cost Price (₹) *</Label>
                    <Input
                      type="number"
                      value={formData.cost_price}
                      onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                      placeholder="0"
                      className="input-admin"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Packing Charges (₹)</Label>
                    <Input
                      type="number"
                      value={formData.packing_charges}
                      onChange={(e) => setFormData({ ...formData, packing_charges: e.target.value })}
                      placeholder="0"
                      className="input-admin"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Wholesale Price (₹)</Label>
                    <Input
                      type="number"
                      value={formData.wholesale_price}
                      onChange={(e) => setFormData({ ...formData, wholesale_price: e.target.value })}
                      placeholder="Optional"
                      className="input-admin"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Qty for Wholesale</Label>
                    <Input
                      type="number"
                      value={formData.wholesale_min_qty}
                      onChange={(e) => setFormData({ ...formData, wholesale_min_qty: e.target.value })}
                      placeholder="10"
                      className="input-admin"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Discount (%)</Label>
                    <Input
                      type="number"
                      value={formData.max_discount_amount}
                      onChange={(e) => setFormData({ ...formData, max_discount_amount: e.target.value })}
                      placeholder="e.g. 10"
                      className="input-admin"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Stock Quantity</Label>
                    <Input
                      type="number"
                      value={formData.stock_qty}
                      onChange={(e) => setFormData({ ...formData, stock_qty: e.target.value })}
                      placeholder="0"
                      className="input-admin"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Low Stock Threshold</Label>
                    <Input
                      type="number"
                      value={formData.low_stock_threshold}
                      onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                      placeholder="10"
                      className="input-admin"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Product Images</Label>
                  <MultipleImageUpload
                    value={formData.images}
                    onChange={(images) => setFormData({ ...formData, images })}
                    folder="products"
                    imageType="product"
                    maxFiles={5}
                    label=""
                    description="Upload product images (max 5 images, 5MB each)"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label>Active</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active_home}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active_home: checked })}
                    />
                    <Label>Show on Home Page</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active_pos}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active_pos: checked })}
                    />
                    <Label>Show in POS</Label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setShowDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="save-product-btn">
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-admin"
              data-testid="product-search"
            />
          </div>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger className="w-[180px] input-admin">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="text-xs">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              View Category IDs
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle>Category IDs Reference</DialogTitle>
              <DialogDescription className="text-slate-400">
                Use these IDs in your CSV file while importing products.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              <p className="text-sm text-slate-400 mb-3">Use these IDs in your CSV file:</p>
              {categories.map((category) => (
                <div key={category.id} className="flex justify-between items-center p-2 bg-slate-700/50 rounded text-sm">
                  <div>
                    <span style={{ paddingLeft: `${category.level * 12}px` }}>
                      {category.full_path}
                    </span>
                    <div className="text-xs text-slate-500">Level {category.level}</div>
                  </div>
                  <code className="bg-slate-600 px-2 py-1 rounded text-xs">{category.id}</code>
                </div>
              ))}
              {categories.length === 0 && (
                <p className="text-sm text-slate-500">No categories found. Create categories first.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Products Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={filteredProducts.length > 0 && selectedProducts.size === filteredProducts.length}
                    onChange={toggleAllSelection}
                    className="rounded border-slate-600 bg-slate-700 text-primary focus:ring-primary h-4 w-4"
                  />
                </TableHead>
                <TableHead className="text-slate-400">Product</TableHead>
                <TableHead className="text-slate-400">SKU</TableHead>
                <TableHead className="text-slate-400">Category</TableHead>
                <TableHead className="text-slate-400">Price</TableHead>
                <TableHead className="text-slate-400">Stock</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No products found
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product.id} className="border-slate-700">
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedProducts.has(product.id)}
                        onChange={() => toggleProductSelection(product.id)}
                        className="rounded border-slate-600 bg-slate-700 text-primary focus:ring-primary h-4 w-4"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-700 rounded-lg overflow-hidden">
                          {product.images?.[0] && (
                            <img src={getImageUrl(product.images[0])} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                        <span className="font-medium truncate max-w-[200px]">{product.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-400">
                        {categories.find(c => c.id === product.category_id)?.name || 'No Category'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="font-semibold">₹{product.selling_price}</span>
                        <span className="text-slate-400 text-sm ml-2 line-through">₹{product.mrp}</span>
                        {product.pos_price && (
                          <div className="text-xs text-blue-400">POS: ₹{product.pos_price}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={product.stock_qty <= product.low_stock_threshold ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}>
                        {product.stock_qty}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={product.is_active}
                          onCheckedChange={(checked) => handleToggleStatus(product.id, checked)}
                          className="data-[state=checked]:bg-green-500"
                        />
                        <span className={`text-xs ${product.is_active ? 'text-green-400' : 'text-slate-500'}`}>
                          {product.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => generateBarcode(product)}
                          title="Generate Barcode"
                        >
                          <Barcode className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(product.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              {/* Items per page */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">Items per page:</span>
                <Select
                  value={limit.toString()}
                  onValueChange={(val) => setLimit(Number(val))}
                >
                  <SelectTrigger className="w-[70px] h-8 bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Pagination Info */}
              <div className="text-sm text-slate-400 flex items-center gap-4">
                <span>Page {currentPage} of {totalPages} ({totalProducts} total products)</span>
                {totalPages > 1 && (
                  <span className="text-xs text-slate-500">
                    Tip: Use Ctrl+← / Ctrl+→ for quick navigation
                  </span>
                )}
              </div>

              {/* Pagination Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="border-slate-600 px-3 h-8 text-xs"
                  title="Previous page (Ctrl+←)"
                >
                  <ChevronLeft className="w-3 h-3 mr-1" />
                  Prev
                </Button>

                <div className="flex items-center gap-2 px-1">
                  {/* Jump to page */}
                  <div className="flex items-center gap-2">
                    <Input
                      className="w-16 h-8 text-center bg-slate-800 border-slate-700 px-2 text-sm"
                      placeholder="Page"
                      value={inputPage}
                      onChange={(e) => setInputPage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const pageNum = parseInt(inputPage);
                          if (pageNum >= 1 && pageNum <= totalPages) {
                            fetchData(pageNum);
                            setInputPage('');
                          }
                        }
                      }}
                      title="Enter page number and press Enter"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-slate-400 hover:text-white text-xs"
                      disabled={!inputPage}
                      onClick={() => {
                        const pageNum = parseInt(inputPage);
                        if (pageNum >= 1 && pageNum <= totalPages) {
                          fetchData(pageNum);
                          setInputPage('');
                        }
                      }}
                      title="Go to page"
                    >
                      Go
                    </Button>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="border-slate-600 px-3 h-8 text-xs"
                  title="Next page (Ctrl+→)"
                >
                  Next
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
